/* eslint-disable max-lines */
import { isArray, isFunction } from 'lodash'
import moment from 'moment'
import {
    ALL_PERIODS,
    PERIOD_NEXT_2_YEARS,
    PERIOD_NEXT_2_YEARS_FROM_NOW,
    PERIOD_NEXT_YEAR,
    PERIOD_NEXT_YEAR_FROM_NOW,
    PERIOD_NEXT_6_MONTH,
    PERIOD_NEXT_6_MONTH_FROM_NOW,
    PERIOD_NEXT_3_MONTH,
    PERIOD_NEXT_3_MONTH_FROM_NOW,
    PERIOD_NEXT_MONTH,
    PERIOD_NEXT_MONTH_FROM_NOW,
    PERIOD_NEXT_6_WEEKS,
    PERIOD_NEXT_6_WEEKS_FROM_NOW,
    PERIOD_NEXT_3_WEEKS,
    PERIOD_NEXT_3_WEEKS_FROM_NOW,
    PERIOD_NEXT_WEEK,
    PERIOD_NEXT_WEEK_FROM_NOW,
    PERIOD_THIS_YEAR,
    PERIOD_THIS_MONTH,
    PERIOD_THIS_WEEK,
    PERIOD_NEXT_30_DAYS,
    PERIOD_NEXT_7_DAYS,
    PERIOD_DAY_6_DAYS_LATER,
    PERIOD_DAY_5_DAYS_LATER,
    PERIOD_DAY_4_DAYS_LATER,
    PERIOD_DAY_3_DAYS_LATER,
    PERIOD_DAY_2_DAYS_LATER,
    PERIOD_TOMORROW,
    PERIOD_TODAY,
    PERIOD_YESTERDAY,
    PERIOD_DAY_2_DAYS_BEFORE,
    PERIOD_DAY_3_DAYS_BEFORE,
    PERIOD_DAY_4_DAYS_BEFORE,
    PERIOD_DAY_5_DAYS_BEFORE,
    PERIOD_DAY_6_DAYS_BEFORE,
    PERIOD_LAST_7_DAYS_AND_TOMORROW,
    PERIOD_LAST_7_DAYS,
    PERIOD_LAST_30_DAYS,
    PERIOD_LAST_WEEK,
    PERIOD_LAST_WEEK_TILL_NOW,
    PERIOD_LAST_3_WEEKS,
    PERIOD_LAST_3_WEEKS_TILL_NOW,
    PERIOD_LAST_6_WEEKS,
    PERIOD_LAST_6_WEEKS_TILL_NOW,
    PERIOD_LAST_MONTH,
    PERIOD_LAST_MONTH_TILL_NOW,
    PERIOD_LAST_3_MONTH,
    PERIOD_LAST_3_MONTH_TILL_NOW,
    PERIOD_LAST_6_MONTH,
    PERIOD_LAST_6_MONTH_TILL_NOW,
    PERIOD_LAST_YEAR,
    PERIOD_LAST_YEAR_TILL_NOW,
    PERIOD_LAST_2_YEARS,
    PERIOD_LAST_2_YEARS_TILL_NOW,
    PERIOD_DEFAULT
} from '../constants/datepicker'
import { THROW_INTL_ERROR_MESSAGE } from '../constants/error'
import {
    getNext2Years,
    getNext2YearsFromNow,
    getNextYear,
    getNextYearFromNow,
    getNext6Month,
    getNext6MonthFromNow,
    getNext3Month,
    getNext3MonthFromNow,
    getNextMonth,
    getNextMonthFromNow,
    getNext6Weeks,
    getNext6WeeksFromNow,
    getNext3Weeks,
    getNext3WeeksFromNow,
    getNextWeek,
    getNextWeekFromNow,
    getNext30Days,
    getNext7Days,
    getDay6DaysLater,
    getDay5DaysLater,
    getDay4DaysLater,
    getDay3DaysLater,
    getDay2DaysLater,
    getTomorrow,
    getThisYear,
    getThisMonth,
    getThisWeek,
    getToday,
    getYesterday,
    getDay2DaysBefore,
    getDay3DaysBefore,
    getDay4DaysBefore,
    getDay5DaysBefore,
    getDay6DaysBefore,
    getLast30Days,
    getLast7Days,
    getLast7DaysAndTomorrow,
    getLastWeek,
    getLastWeekTillNow,
    getLast3Weeks,
    getLast3WeeksTillNow,
    getLast6Weeks,
    getLast6WeeksTillNow,
    getLastMonth,
    getLastMonthTillNow,
    getLast3Month,
    getLast3MonthTillNow,
    getLast6Month,
    getLast6MonthTillNow,
    getLastYear,
    getLastYearTillNow,
    getLast2Years,
    getLast2YearsTillNow,
    intervalToDate
} from './interval'

const DEFAULT_PERIODS = [
    PERIOD_TODAY,
    PERIOD_YESTERDAY,
    PERIOD_LAST_7_DAYS,
    PERIOD_LAST_30_DAYS,
    PERIOD_THIS_WEEK,
    PERIOD_LAST_WEEK,
    PERIOD_THIS_MONTH,
    PERIOD_LAST_MONTH
]

/**
 * The resulting datepicker period object.
 *
 * @typedef {Object} DatePickerPeriod
 * @property {string} title - The translated title to be displayed
 * @property {Interval} interval - Start and end time of the period
 * @property {string} key - The period key of the object
 */

/**
 * Creates a period object to be used in the datepicker.
 *
 * @param {string} title - Title for the period
 * @param {Interval} interval - Interval that will define the period's (date) interval
 * @param {string} key - The period key of the object
 * @returns {DatePickerPeriod}
 */
function createDatePickerPeriod(title, interval, key) {
    return { interval: intervalToDate(interval), key, title }
}

/**
 * Creates a period object to be used in the datepicker.
 *
 * @deprecated Use createDatePickerPeriod instead
 * @param {string} title - Title for the period
 * @param {Interval} interval - Interval that will define the period's (date) interval
 * @returns {DatePickerPeriod}
 */
function createPeriod(title, interval) {
    console.warn('createPeriod is deprecated, use createDatePickerPeriod instead.')
    return createDatePickerPeriod(title, interval)
}

/**
 * Get the platform's default period to be used in the datepicker.
 *
 * @param {Intl} intl - Global modux intl helper
 * @returns {DatePickerPeriod}
 */
function getDefaultDatePickerPeriod(intl) {
    return getDatePickerPeriod(PERIOD_DEFAULT, intl)
}

/**
 * Get the platform's default period to be used in the datepicker.
 *
 * @deprecated Use getDefaultDatePickerPeriod instead
 * @param {Intl} intl - Global modux intl helper
 * @returns {DatePickerPeriod}
 */
function getDefaultPeriod(intl) {
    console.warn('getDefaultPeriod is deprecated, use getDefaultDatePickerPeriod instead.')
    return getDefaultDatePickerPeriod(intl)
}

/**
 * Get an array of periods given a list of period keys to be used in the datepicker.
 *
 * @param {Array} periodKeys - Array containing the required periods keys
 * @param {Intl} intl - Global modux intl helper
 * @returns {Array<DatePickerPeriod>} Array containing required periods
 */
function getDatePickerPeriods(periodKeys, intl) {
    if (!periodKeys || !isArray(periodKeys)) {
        return []
    }

    return periodKeys.map(periodKey => getDatePickerPeriod(periodKey, intl)).filter(period => !!period)
}

/**
 * Get an array of periods given a list of period keys to be used in the datepicker.
 *
 * @deprecated Use getDatePickerPeriods instead
 * @param {Array} periodKeys - Array containing the required periods keys
 * @param {Intl} intl - Global modux intl helper
 * @returns {Array<DatePickerPeriod>} Array containing required periods
 */
function getPeriods(periodKeys, intl) {
    console.warn('getPeriods is deprecated, use getDatePickerPeriods instead.')
    return getDatePickerPeriods(periodKeys, intl)
}

/**
 * Get all possible periods for the datepicker.
 *
 * @param {Intl} intl - Global modux intl helper
 * @returns {Array<DatePickerPeriod>} Array containing all the periods identified by a key
 */
function getAllDatePickerPeriods(intl) {
    return getDatePickerPeriods(ALL_PERIODS, intl)
}

/**
 * Get all possible periods for the datepicker.
 *
 * @deprecated Use getAllDatePickerPeriods instead
 * @param {Intl} intl - Global modux intl helper
 * @returns {Array<DatePickerPeriod>} Array containing all the periods identified by a key
 */
function getAllPeriods(intl) {
    console.warn('getAllPeriods is deprecated, use getAllDatePickerPeriods instead.')
    return getAllDatePickerPeriods(intl)
}

/**
 * Get platform default periods for the datepicker.
 *
 * @param {Intl} intl - Global modux intl helper
 * @returns {Array<DatePickerPeriod>} Array containing all default periods identified by a key
 */
function getDefaultDatePickerPeriods(intl) {
    return getDatePickerPeriods(DEFAULT_PERIODS, intl)
}

/**
 * Get platform default periods for the datepicker.
 *
 * @deprecated Use getDefaultDatePickerPeriods instead
 * @param {Intl} intl - Global modux intl helper
 * @returns {Array<DatePickerPeriod>} Array containing all default periods identified by a key
 */
function getDefaultPeriods(intl) {
    console.warn('getDefaultPeriods is deprecated, use getDefaultDatePickerPeriods instead.')
    return getDefaultDatePickerPeriods(intl)
}

/**
 * Return a defined datepicker period by it's key.
 *
 * @param {string} periodKey - One of the valid period keys
 * @param {Intl} intl - Global modux intl helper
 * @returns {DatePickerPeriod}
 */
function getDatePickerPeriod(periodKey, intl) {
    if (!intl || !isFunction(intl.translate)) {
        throw new Error(THROW_INTL_ERROR_MESSAGE)
    }

    const translate = intl.translate

    switch (periodKey) {
        case PERIOD_NEXT_2_YEARS:
            return createDatePickerPeriod(
                translate('atl_period-next-x-years', { years: 2 }),
                getNext2Years(),
                periodKey
            )
        case PERIOD_NEXT_2_YEARS_FROM_NOW:
            return createDatePickerPeriod(
                translate('atl_period-next-x-years-from-now', { years: 2 }),
                getNext2YearsFromNow(),
                periodKey
            )
        case PERIOD_NEXT_YEAR:
            return createDatePickerPeriod(translate('atl_period-next-year'), getNextYear(), periodKey)
        case PERIOD_NEXT_YEAR_FROM_NOW:
            return createDatePickerPeriod(translate('atl_period-next-year-from-now'), getNextYearFromNow(), periodKey)
        case PERIOD_NEXT_6_MONTH:
            return createDatePickerPeriod(
                translate('atl_period-next-x-month', { month: 6 }),
                getNext6Month(),
                periodKey
            )
        case PERIOD_NEXT_6_MONTH_FROM_NOW:
            return createDatePickerPeriod(
                translate('atl_period-next-x-month-from-now', { month: 6 }),
                getNext6MonthFromNow(),
                periodKey
            )
        case PERIOD_NEXT_3_MONTH:
            return createDatePickerPeriod(
                translate('atl_period-next-x-month', { month: 3 }),
                getNext3Month(),
                periodKey
            )
        case PERIOD_NEXT_3_MONTH_FROM_NOW:
            return createDatePickerPeriod(
                translate('atl_period-next-x-month-from-now', { month: 3 }),
                getNext3MonthFromNow(),
                periodKey
            )
        case PERIOD_NEXT_MONTH:
            return createDatePickerPeriod(translate('atl_period-next-month'), getNextMonth(), periodKey)
        case PERIOD_NEXT_MONTH_FROM_NOW:
            return createDatePickerPeriod(translate('atl_period-next-month-from-now'), getNextMonthFromNow(), periodKey)
        case PERIOD_NEXT_6_WEEKS:
            return createDatePickerPeriod(
                translate('atl_period-next-x-weeks', { weeks: 6 }),
                getNext6Weeks(),
                periodKey
            )
        case PERIOD_NEXT_6_WEEKS_FROM_NOW:
            return createDatePickerPeriod(
                translate('atl_period-next-x-weeks-from-now', { weeks: 6 }),
                getNext6WeeksFromNow(),
                periodKey
            )
        case PERIOD_NEXT_3_WEEKS:
            return createDatePickerPeriod(
                translate('atl_period-next-x-weeks', { weeks: 3 }),
                getNext3Weeks(),
                periodKey
            )
        case PERIOD_NEXT_3_WEEKS_FROM_NOW:
            return createDatePickerPeriod(
                translate('atl_period-next-x-weeks-from-now', { weeks: 3 }),
                getNext3WeeksFromNow(),
                periodKey
            )
        case PERIOD_NEXT_WEEK:
            return createDatePickerPeriod(translate('atl_period-next-week'), getNextWeek(), periodKey)
        case PERIOD_NEXT_WEEK_FROM_NOW:
            return createDatePickerPeriod(translate('atl_period-next-week-from-now'), getNextWeekFromNow(), periodKey)
        case PERIOD_NEXT_30_DAYS:
            return createDatePickerPeriod(translate('atl_period_next_days', { days: 30 }), getNext30Days(), periodKey)
        case PERIOD_NEXT_7_DAYS:
            return createDatePickerPeriod(translate('atl_period_next_days', { days: 7 }), getNext7Days(), periodKey)
        case PERIOD_DAY_6_DAYS_LATER:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-later', { days: 6 }),
                getDay6DaysLater(),
                periodKey
            )
        case PERIOD_DAY_5_DAYS_LATER:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-later', { days: 5 }),
                getDay5DaysLater(),
                periodKey
            )
        case PERIOD_DAY_4_DAYS_LATER:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-later', { days: 4 }),
                getDay4DaysLater(),
                periodKey
            )
        case PERIOD_DAY_3_DAYS_LATER:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-later', { days: 3 }),
                getDay3DaysLater(),
                periodKey
            )
        case PERIOD_DAY_2_DAYS_LATER:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-later', { days: 2 }),
                getDay2DaysLater(),
                periodKey
            )
        case PERIOD_TOMORROW:
            return createDatePickerPeriod(translate('atl_period-tomorrow'), getTomorrow(), periodKey)
        case PERIOD_THIS_YEAR:
            return createDatePickerPeriod(translate('atl_period-this-year'), getThisYear(), periodKey)
        case PERIOD_THIS_MONTH:
            return createDatePickerPeriod(translate('atl_period_current_month'), getThisMonth(), periodKey)
        case PERIOD_THIS_WEEK:
            return createDatePickerPeriod(
                translate('atl_period_current_week'),
                getThisWeek(moment().locale(intl.getLocale())),
                periodKey
            )
        case PERIOD_TODAY:
            return createDatePickerPeriod(translate('atl_period_today'), getToday(), periodKey)
        case PERIOD_YESTERDAY:
            return createDatePickerPeriod(translate('atl_period_yesterday'), getYesterday(), periodKey)
        case PERIOD_DAY_2_DAYS_BEFORE:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-before', { days: 2 }),
                getDay2DaysBefore(),
                periodKey
            )
        case PERIOD_DAY_3_DAYS_BEFORE:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-before', { days: 3 }),
                getDay3DaysBefore(),
                periodKey
            )
        case PERIOD_DAY_4_DAYS_BEFORE:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-before', { days: 4 }),
                getDay4DaysBefore(),
                periodKey
            )
        case PERIOD_DAY_5_DAYS_BEFORE:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-before', { days: 5 }),
                getDay5DaysBefore(),
                periodKey
            )
        case PERIOD_DAY_6_DAYS_BEFORE:
            return createDatePickerPeriod(
                translate('atl_period-day-x-days-before', { days: 6 }),
                getDay6DaysBefore(),
                periodKey
            )
        case PERIOD_LAST_7_DAYS_AND_TOMORROW:
            return createDatePickerPeriod(
                translate('atl_period_last_days_and_tomorrow', { days: 7 }),
                getLast7DaysAndTomorrow(),
                periodKey
            )
        case PERIOD_LAST_7_DAYS:
            return createDatePickerPeriod(translate('atl_period_last_days', { days: 7 }), getLast7Days(), periodKey)
        case PERIOD_LAST_30_DAYS:
            return createDatePickerPeriod(translate('atl_period_last_days', { days: 30 }), getLast30Days(), periodKey)
        case PERIOD_LAST_WEEK:
            return createDatePickerPeriod(translate('atl_period_last_week'), getLastWeek(), periodKey)
        case PERIOD_LAST_WEEK_TILL_NOW:
            return createDatePickerPeriod(translate('atl_period-last-week-till-now'), getLastWeekTillNow(), periodKey)
        case PERIOD_LAST_3_WEEKS:
            return createDatePickerPeriod(
                translate('atl_period-last-x-weeks', { weeks: 3 }),
                getLast3Weeks(),
                periodKey
            )
        case PERIOD_LAST_3_WEEKS_TILL_NOW:
            return createDatePickerPeriod(
                translate('atl_period-last-x-weeks-till-now', { weeks: 3 }),
                getLast3WeeksTillNow(),
                periodKey
            )
        case PERIOD_LAST_6_WEEKS:
            return createDatePickerPeriod(
                translate('atl_period-last-x-weeks', { weeks: 6 }),
                getLast6Weeks(),
                periodKey
            )
        case PERIOD_LAST_6_WEEKS_TILL_NOW:
            return createDatePickerPeriod(
                translate('atl_period-last-x-weeks-till-now', { weeks: 6 }),
                getLast6WeeksTillNow(),
                periodKey
            )
        case PERIOD_LAST_MONTH:
            return createDatePickerPeriod(translate('atl_period_last_month'), getLastMonth(), periodKey)
        case PERIOD_LAST_MONTH_TILL_NOW:
            return createDatePickerPeriod(translate('atl_period-last-month-till-now'), getLastMonthTillNow(), periodKey)
        case PERIOD_LAST_3_MONTH:
            return createDatePickerPeriod(
                translate('atl_period-last-x-month', { month: 3 }),
                getLast3Month(),
                periodKey
            )
        case PERIOD_LAST_3_MONTH_TILL_NOW:
            return createDatePickerPeriod(
                translate('atl_period-last-x-month-till-now', { month: 3 }),
                getLast3MonthTillNow(),
                periodKey
            )
        case PERIOD_LAST_6_MONTH:
            return createDatePickerPeriod(
                translate('atl_period-last-x-month', { month: 6 }),
                getLast6Month(),
                periodKey
            )
        case PERIOD_LAST_6_MONTH_TILL_NOW:
            return createDatePickerPeriod(
                translate('atl_period-last-x-month-till-now', { month: 6 }),
                getLast6MonthTillNow(),
                periodKey
            )
        case PERIOD_LAST_YEAR:
            return createDatePickerPeriod(translate('atl_period-last-year'), getLastYear(), periodKey)
        case PERIOD_LAST_YEAR_TILL_NOW:
            return createDatePickerPeriod(translate('atl_period-last-year-till-now'), getLastYearTillNow(), periodKey)
        case PERIOD_LAST_2_YEARS:
            return createDatePickerPeriod(
                translate('atl_period-last-x-years', { years: 2 }),
                getLast2Years(),
                periodKey
            )
        case PERIOD_LAST_2_YEARS_TILL_NOW:
            return createDatePickerPeriod(
                translate('atl_period-last-x-years-till-now', { years: 2 }),
                getLast2YearsTillNow(),
                periodKey
            )
        default:
            return null
    }
}

/**
 * Return a defined datepicker period by it's key.
 *
 * @deprecated Use getDatePickerPeriod instead
 * @param {string} periodKey - One of the valid period keys
 * @param {Intl} intl - Global modux intl helper
 * @returns {DatePickerPeriod}
 */
function getPeriod(periodKey, intl) {
    console.warn('getPeriod is deprecated, use getDatePickerPeriod instead.')
    return getDatePickerPeriod(periodKey, intl)
}

export {
    DEFAULT_PERIODS,
    createDatePickerPeriod,
    createPeriod,
    getDefaultDatePickerPeriod,
    getDefaultPeriod,
    getDefaultPeriods,
    getDatePickerPeriods,
    getPeriods,
    getAllDatePickerPeriods,
    getAllPeriods,
    getDefaultDatePickerPeriods,
    getDatePickerPeriod,
    getPeriod
}
